import React, { useState, useEffect } from 'react';
import { formatFileSize, downloadFile } from '../../utils/mediaHelpers';
import { useBinaryMedia } from '../../utils/binaryMediaFetcher';

const ImageMessage = ({ media, caption, isSender }) => {
  const [showLightbox, setShowLightbox] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Use binary media hook for proper blob fetching
  const { url: imageUrl, blobSize, isLoading, error: fetchError, errorDetails } = useBinaryMedia(
    media?.url,
    'image/jpeg,image/png,image/webp,image/gif,image/svg+xml,image/heic,*/*'
  );

  // Handle image loading errors
  useEffect(() => {
    if (fetchError) {
      setImageError(true);
    }
  }, [fetchError]);

  const handleImageLoad = () => {
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleDownload = () => {
    downloadFile(media.url, media.originalName);
  };

  const handleImageClick = () => {
    if (!isLoading && !imageError) {
      setShowLightbox(true);
    }
  };

  const closeLightbox = () => {
    setShowLightbox(false);
  };

  return (
    <>
      <div className={`media-message image-message ${isSender ? 'sender' : 'receiver'}`} style={{ width: '100%', maxWidth: '300px' }}>
        <div className="media-content">
          {/* Loading state */}
          {isLoading && (
            <div className="media-loading">
              <div className="loading-spinner"></div>
              <span>Loading image...</span>
              {blobSize > 0 && (
                <div className="image-info">
                  <small>Size: {formatFileSize(blobSize)}</small>
                </div>
              )}
            </div>
          )}

          {/* Error state */}
          {(imageError || fetchError) && (
            <div className="media-error">
              <div className="error-icon">❌</div>
              <span>Failed to load image</span>
              {errorDetails && (
                <div className="error-details">
                  <small>{errorDetails}</small>
                </div>
              )}
              <div className="error-actions">
                <button
                  className="download-fallback-btn"
                  onClick={handleDownload}
                  title="Download image file"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                  </svg>
                  Download Image
                </button>
              </div>
            </div>
          )}

          {/* Image */}
          {!isLoading && !imageError && imageUrl && (
            <div className="image-container" onClick={handleImageClick}>
              <img
                src={imageUrl}
                alt={caption || media.originalName}
                className="image-preview"
                onLoad={handleImageLoad}
                onError={handleImageError}
                loading="lazy"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain'
                }}
              />
            </div>
          )}

          {/* Download button */}
          {!isLoading && !imageError && imageUrl && (
            <button
              className="media-download-btn"
              onClick={handleDownload}
              title="Download image"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
              </svg>
            </button>
          )}
        </div>

        {/* Metadata - timestamp only */}
        <div className="media-info">
          <div className="media-metadata">
            {blobSize > 0 ? (
              <span className="file-size">{formatFileSize(blobSize)}</span>
            ) : media.size > 0 ? (
              <span className="file-size">{formatFileSize(media.size)}</span>
            ) : null}
          </div>
          <div className="message-time">
            {new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {showLightbox && imageUrl && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>

            <div className="lightbox-image-container">
              <img
                src={imageUrl}
                alt={caption || media.originalName}
                className="lightbox-image"
                style={{
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  objectFit: 'contain'
                }}
              />
            </div>

            <div className="lightbox-controls">
              <button
                className="lightbox-download"
                onClick={handleDownload}
                title="Download image"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                </svg>
                Download
              </button>

              {caption && (
                <div className="lightbox-caption">
                  {caption}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageMessage;
